import { parseJsExprSafe } from './acorn';
import { fromPairs } from "lodash-es";
import detectGlobals from './detectGlobals';
import MagicString from 'magic-string';
import { newFunction } from './newFunction';
import * as AcornWalk from 'acorn-walk';
const leadingCommentRegex = /^\s*(?:\/\/[^\n]*|\/\*[\d\D]*?(?:\*\/|$))?/;
/**
 * 测量一段 JS 代码开头的空白字符 + 注释的字符数量
 *
 * @param code
 */
export function measureLeadingComments(code) {
  let counter = 0;
  let tmpC = code;
  let tmpM;
  while (tmpM = leadingCommentRegex.exec(tmpC)) {
    const dLen = tmpM[0].length;
    if (!dLen) break;
    counter += dLen;
    tmpC = tmpC.substr(dLen);
  }
  return counter;
}
/**
 * 一开始，为了方便大家写插值表达式，允许省略 `return` 直接写右值，但是后来发现还是存在混用的情况。
 *
 * 这里提供了一个方法，按需在代码前添加 `return (...)` 的 关键词
 *
 * 不做语法检查，出来的代码可能跑不了哦~
 *
 * @param code 可以直接是一个表达式（例如 `{ foo: bar }`）也可以是函数体（比如 `return { foo: bar }`）
 */
export function addReturnKeywordIfNeeded(code) {
  try {
    const tempCode = `return (\n${code}\n);`;
    // eslint-disable-next-line no-new-func
    newFunction([], tempCode); // 如果有语法错误会抛出
    return tempCode;
  } catch (err) {
    /* istanbul ignore else */
    if (err instanceof SyntaxError) return code;
    /* istanbul ignore next */
    throw err;
  }
}
export function isExpression(code) {
  try {
    const tempCode = `return (\n${code}\n);`;
    // eslint-disable-next-line no-new-func
    new Function(tempCode); // 如果有语法错误会抛出
    return true;
  } catch (err) {
    /* istanbul ignore else */
    if (err instanceof SyntaxError) return false;
    /* istanbul ignore next */
    throw err;
  }
}
/**
 * 如果代码是一个函数体，就变成 IIFE 的形式（`(()=>{...})()`），否则，将代码视为表达式，就前后加个圆括号（比如 `(foo.name)` ）
 *
 * 生成的东西可以用于表达式中，比如前面加个 `return` 关键词，或者直接放到中括号里等
 */
export function toExpression(code) {
  if (isExpression(code)) return `(${code}\n)`;
  return `(() => {${code}\n})()`;
}
/**
 * 对一段 JS 代码做词法解析，然后为全局变量添加一个前缀（比如 `jQuery` => `window.jQuery`），
 * 或者直接替换全局变量（比如 `this` => `window`）
 *
 * @param processIdentifier 一个 string => string 的函数，定义了如何做替换。
 *                          比如 `"jQuery"` => `"window.jQuery"`
 *                          （需要注意，这个函数也会拿到 `"this"`，请按需转换）
 *
 * @param allowedList 可选，保留某些全局变量不被加前缀。默认 `Math` `window` 等会被忽略
 */
export function processGlobalVars(processIdentifier, code, allowedList) {
  const parsed = parseJsExprSafe(code);
  if (!parsed.ast) throw parsed.error;
  const processed = new MagicString(code);
  const isSkipped = name => {
    if (allowedGlobalVars[name]) return true;
    if (Array.isArray(allowedList) && allowedList.includes(name)) return true;
    if (typeof allowedList === 'function' && allowedList(name)) return true;
    return false;
  };
  const globals = detectGlobals(parsed.ast);
  // eslint-disable-next-line no-restricted-syntax
  for (const global of globals) {
    if (isSkipped(global.name)) continue;
    global.nodes.forEach(node => {
      const newIdentifier = processIdentifier(global.name);
      processed.overwrite(node.start - parsed.offset, node.end - parsed.offset, newIdentifier);
    });
  }
  return String(processed);
}
const ignore = () => void 0;
const findOutReturnStmtVisitor = AcornWalk.make({
  FunctionDeclaration: ignore,
  FunctionExpression: ignore,
  ArrowFunctionExpression: ignore,
  ClassDeclaration: ignore,
  ClassExpression: ignore,
  ClassBody: ignore,
  MethodDefinition: ignore
});
export function findOutReturnStatements(ast) {
  const ans = [];
  try {
    ast && AcornWalk.simple(ast, {
      ReturnStatement: node => ans.push(node)
    }, findOutReturnStmtVisitor);
  } catch (Err) {}
  return ans;
}
const allowedGlobalVars = fromPairs(('Infinity,undefined,NaN,isFinite,isNaN,' + 'parseFloat,parseInt,decodeURI,decodeURIComponent,encodeURI,encodeURIComponent,URLSearchParams,' + 'Math,Number,Date,Array,Object,Boolean,Symbol,String,RegExp,Map,Set,JSON,Intl,BigInt,' + 'require,window,console,document,globalThis').split(',').map(x => [x, true]));
//# sourceMappingURL=processScriptContent.js.map
